import type { ReactNode } from 'react'
import { cssVars } from '../cssVars'
import { formatCount, formatDelta, formatDeltaShare, formatShare } from '../format'
import { Sparkline } from '../charts/Sparkline/Sparkline'
import styles from './Metric.module.css'

/**
 * One figure, said properly.
 *
 * The denominator is the reason this component is not four lines of markup.
 *
 * The platform model is explicit, and it is the clearest finding in the
 * evidence behind this product: a broadcast reports how many people *played* a
 * message, and the number sent appears only underneath it as the denominator.
 * MomConnect registered over a million women and fewer than six per cent were
 * still receiving anything by six to twelve months. Kilkari reached 95% of its
 * group, 65% listened to half, and the trial found no effect on the behaviour
 * it existed to change. "1,200 sent" is a number that has repeatedly meant
 * nothing, and a dashboard that shows it alone is not neutral: it is telling an
 * organization it succeeded.
 *
 * So the shape of this component is the argument. Pass `of` and the numerator
 * takes the large serif, the denominator takes small muted text beside it, the
 * share is computed rather than typed, and a bar shows the share as a
 * proportion of the whole. There is no prop that makes the denominator the
 * headline, and the props are typed so that a denominator without a countable
 * numerator does not compile.
 *
 * A runtime guard that warned when a metric was labelled "sent" with no `of`
 * was considered and rejected. It checks spelling rather than meaning, it fires
 * on a correct metric that happens to be about postage, and it goes silent the
 * day the console is translated.
 */

export type MetricIntent = 'higher_is_better' | 'lower_is_better' | 'neutral'

export interface MetricDelta {
  /** The change, in the metric's own units. Signed. */
  change: number
  /** What it changed from. Present, a share is shown alongside the change. */
  previous?: number
  /** The comparison: "vs last week", "since the baseline". */
  label?: ReactNode
  /**
   * Which way is good. The vocabulary is the indicators model's own
   * `higher_is_better | lower_is_better`, so a metric bound to an indicator can
   * pass the field straight through instead of translating it into a local word
   * that then has to be kept in step.
   */
  intent?: MetricIntent
}

export interface MetricTrend {
  values: readonly number[]
  /** One label per value: a week, a month, a reporting period. */
  labels?: readonly string[]
  /**
   * Names the line for a screen reader. Required, and grouped with the values
   * for that reason: a title passed as a sibling prop is a title that can be
   * left out, and the sparkline then draws an unnamed strip with no way to find
   * out what it is a picture of.
   */
  title: string
}

interface MetricBaseProps {
  label: string
  hint?: ReactNode
  delta?: MetricDelta
  /** A shape behind the figure. The figure carries the magnitude. */
  trend?: MetricTrend
  locale?: string
  className?: string
}

export type MetricProps = MetricBaseProps &
  (
    | {
        /** A count, or an already-formatted string for things like durations. */
        value: number | string
        of?: never
        ofLabel?: never
      }
    | {
        value: number
        /** The whole this figure is a part of. Rendered small, always. */
        of: number
        /** What the whole is: "sent", "members", "enrolled". */
        ofLabel?: string
      }
  )

/*
 * Taken as one `props` object and destructured inside, rather than destructured
 * in the signature. `MetricProps` is a union, and destructuring a union in the
 * parameter list collapses it: TypeScript widens `value` and stops relating it
 * to `of`, which is the relationship the whole type exists to enforce.
 */
export function Metric(props: MetricProps) {
  const { label, value, of, ofLabel, hint, delta, trend, locale, className } = props

  const formattedValue = typeof value === 'number' ? formatCount(value, locale) : value
  const share =
    typeof value === 'number' && of !== undefined ? formatShare(value, of, locale) : null
  const sharePercent =
    typeof value === 'number' && of !== undefined && of > 0
      ? Math.min(100, Math.max(0, (value / of) * 100))
      : null

  return (
    <div className={[styles.metric, className].filter(Boolean).join(' ')}>
      <p className={styles.label}>{label}</p>

      <p className={styles.figure}>
        <span className={styles.value}>{formattedValue}</span>
        {of !== undefined ? (
          /*
           * A slash, not the word "of". This component holds no copy, and "of"
           * is copy: writing it here would put one English word inside every
           * translated console. A screen reader reads the mark as "slash",
           * which is the fraction the figure actually is.
           */
          <span className={styles.denominator}>
            {`/ ${formatCount(of, locale)}`}
            {ofLabel ? ` ${ofLabel}` : null}
          </span>
        ) : null}
      </p>

      {share !== null && sharePercent !== null ? (
        <div className={styles.share}>
          {/* The bar is the share drawn, and the figure beside it is the share
              written. Neither is alone: a proportion carried only by a length
              cannot be read off precisely, and a percentage alone does not show
              how much of the whole is missing. */}
          <span className={styles.bar} style={cssVars({ '--share': `${sharePercent}%` })} />
          <span className={styles.shareValue}>{share}</span>
        </div>
      ) : null}

      {delta ? <Delta delta={delta} locale={locale} /> : null}

      {hint ? <p className={styles.hint}>{hint}</p> : null}

      {trend ? (
        <div className={styles.trend}>
          <Sparkline
            values={trend.values}
            labels={trend.labels}
            title={trend.title}
            locale={locale}
          />
        </div>
      ) : null}
    </div>
  )
}

function Delta({ delta, locale }: { delta: MetricDelta; locale?: string }) {
  const { change, previous, label, intent = 'neutral' } = delta
  const rose = change > 0
  const movement = change === 0 ? 'flat' : rose ? 'up' : 'down'
  const welcome = intent === 'higher_is_better' ? rose : !rose
  const tone = intent === 'neutral' || change === 0 ? 'neutral' : welcome ? 'good' : 'bad'

  const asShare = previous === undefined ? null : formatDeltaShare(change, previous, locale)

  return (
    <p className={styles.delta} data-tone={tone}>
      {/*
        The arrow points the way the number moved, never the way it should have
        moved. On a lower-is-better indicator a fall is good, and it is drawn
        falling and coloured as good: an arrow that pointed at "good" would be a
        picture that contradicts its own figure.

        Tone is carried by colour and by the arrow's shape together. Colour on
        its own would say nothing to a person who cannot separate the two.
      */}
      <DeltaGlyph movement={movement} />
      <span className={styles.deltaValue}>{formatDelta(change, locale)}</span>
      {asShare ? <span className={styles.deltaShare}>{asShare}</span> : null}
      {label ? <span className={styles.deltaLabel}>{label}</span> : null}
    </p>
  )
}

/**
 * Drawn locally, to the same rules as every other glyph in both applications: a
 * 1.6 unit hairline on a 24 unit grid, round caps, no fill, no accessible name
 * of its own because the change is written out beside it.
 */
function DeltaGlyph({ movement }: { movement: 'up' | 'down' | 'flat' }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable={false}
    >
      {movement === 'flat' ? (
        <path d="M5 12h14" />
      ) : movement === 'up' ? (
        <>
          <path d="M12 19V6" />
          <path d="M6 12l6-6 6 6" />
        </>
      ) : (
        <>
          <path d="M12 5v13" />
          <path d="M6 12l6 6 6-6" />
        </>
      )}
    </svg>
  )
}
